import React from "react";
import { Box, Typography, Avatar, Grid, ListItem, List, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import moment from 'moment';
import RectangleIcon from "@mui/icons-material/Rectangle";

interface SolutionOwnerInfo {
    id: string;
    name: string;
    email?: string;
    googleProfilePicture?: string | null;
}

interface DataScan {
    id: string;
    scanName: string;
    scanType: string;
    targetTable: string;
    projectId: string;
    cloudProvider: string;
    status: string;
    updatedAt?: string;
    createdAt?: string;
    solutionOwner?: SolutionOwnerInfo | null;
    reasonForRejection?: string;
}

interface DataScansTableProps {
    dataScans: DataScan[];
    columnFilters: Record<string, boolean>;
    statusType?: 'pending' | 'active' | 'rejected';
}

const DataScansTable: React.FC<DataScansTableProps> = ({
    dataScans,
    columnFilters,
    statusType,
}) => {
    const [cardHoverIndex, setCardHoverIndex] = React.useState<number | null>(null);
    const navigate = useNavigate();

    const handleRowClick = (id: string) => {
        navigate(`/datasolutions/data-scans/details/${id}`);
    };

    const dateFormatter = (dateValue: string | number | null | undefined) => {
        if (!dateValue) return null;
        const numericDate = Number(dateValue);
        if (isNaN(numericDate)) return null;
        return moment(new Date(numericDate).toISOString()).format("DD/MM/YYYY | hh:mm A");
    };

    const headers = [
        { key: "scanName", label: "SCAN NAME", xs: 2 },
        { key: "scanType", label: "TYPE", xs: 1.5 },
        { key: "cloudProvider", label: "CLOUD IDENTIFIER", xs: 1.5 },
        { key: "targetTable", label: "TARGET", xs: 2 },
        { key: "projectId", label: "PROJECT ID", xs: 1.5 },
        { key: "solutionOwner", label: "OWNER", xs: 1.5 },
        { key: "createdAt", label: "ADDED ON", xs: 2 },
    ];

    const iconColor = statusType === "active" ? "#12B76A" : statusType === "rejected" ? "#F04438" : "#FDB022";
    const listItemBgColor = statusType === "active" ? "#FFFFFF" : statusType === "rejected" ? "#F0443810" : "#FDB02210";

    return (
        <List sx={{ bgcolor: "transparent", px: 0, mx: 0, width: '100%' }}>
            <ListItem sx={{ bgcolor: "#f2f2f2", borderRadius: "4px", mb: 0.5, px: 2, py: 1, width: '100%' }}>
                <Box width="100%">
                    <Grid container direction="row" alignItems="center">
                        {headers.map(header => (
                            columnFilters[header.key] && (
                                <Grid item xs={header.xs as any} key={header.key}>
                                    <Typography fontSize="12px" color="#000000" fontFamily="Gilroy-Medium">
                                        {header.label}
                                    </Typography>
                                </Grid>
                            )
                        ))}
                    </Grid>
                </Box>
            </ListItem>

            {dataScans.map((scan, index) => (
                <ListItem
                    key={scan.id}
                    sx={{
                        mt: 1,
                        borderRadius: "8px",
                        cursor: "pointer",
                        px: 2,
                        width: '100%',
                        backgroundColor: listItemBgColor,
                        '&:hover': { borderColor: "black" }
                    }}
                    onMouseEnter={() => setCardHoverIndex(index)}
                    onMouseLeave={() => setCardHoverIndex(null)}
                    onClick={() => handleRowClick(scan.id)}
                >
                    <Box width="100%">
                        <Grid container direction="row" alignItems="center">
                            {columnFilters.scanName && (
                                <Grid item xs={headers.find(h => h.key === 'scanName')?.xs as any}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <RectangleIcon sx={{ color: iconColor, bgcolor: iconColor, width: "4px", height: "22px", marginRight: "8px" }} />
                                        <Typography fontSize="14px" fontFamily="Gilroy-Medium" color="text.primary" noWrap>
                                            {scan.scanName || "N/A"}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            {columnFilters.scanType && (
                                <Grid item xs={headers.find(h => h.key === 'scanType')?.xs as any}>
                                    <Grid item xs={1.5}><Typography fontSize="14px" fontFamily="Gilroy-Regular">{scan.scanType || 'N/A'}</Typography></Grid>
                                </Grid>
                            )}
                            {columnFilters.cloudProvider && (
                                <Grid item xs={headers.find(h => h.key === 'cloudProvider')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" sx={{textTransform: 'uppercase'}}>
                                        {scan.cloudProvider || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                            {columnFilters.targetTable && (
                                <Grid item xs={headers.find(h => h.key === 'targetTable')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                        {scan.targetTable || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                            {columnFilters.projectId && (
                                <Grid item xs={headers.find(h => h.key === 'projectId')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                        {scan.projectId || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                            
                            {columnFilters.solutionOwner && (
                                <Grid item xs={headers.find(h => h.key === 'solutionOwner')?.xs as any}>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <Avatar
                                            src={scan.solutionOwner?.googleProfilePicture || undefined}
                                            alt={scan.solutionOwner?.name || ""}
                                            sx={{ marginRight: "8px", height: 24, width: 24, fontSize: '0.75rem', bgcolor: scan.solutionOwner?.googleProfilePicture ? 'transparent' : 'rgba(0,0,0,0.1)' }}
                                        >
                                            {scan.solutionOwner && !scan.solutionOwner.googleProfilePicture ? scan.solutionOwner.name.charAt(0).toUpperCase() : null}
                                        </Avatar>
                                        <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                            {scan.solutionOwner?.name || "N/A"}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            {columnFilters.createdAt && (
                                <Grid item xs={headers.find(h => h.key === 'createdAt')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333">
                                        {dateFormatter(Number(scan.createdAt))}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </ListItem>
            ))}
        </List>
    );
};

export default DataScansTable;